import { Drawer } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ATTACHMENT_TYPES,
  DATE_FORMAT,
  MILTIS_SOCIAL_REGIMES,
  RELATIONSHIP,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import { bindDateInputProps } from "utils/helpers/input";
import validation from "./validation";
import ProductCard from "./widgets/ProductCard";

import { FaMinus, FaPlus } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import {
  hundredYearsAgoStart,
  isMajor,
  twentySevenYearsAgoStart,
} from "utils/helpers/date";
import {
  resolveChildSocialRegimeForPricing,
  shouldAskChildrenSocialRegime,
} from "utils/helpers/miltisRelatedPersonChild";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Label from "../Components/Label";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import Swiper from "../Components/Swiper";

const View = ({
  onSubmit,
  onQuoteSubmit,
  products,
  disabledProducts,
  contact,
  refetchCosts,
  downloadSingleDocument,
  locked,
  duplicateProject,
  onBack,
  refetchLoading,
  quoteLoading,
  territorial = false,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerGuaranteeVisible, setDrawerGuaranteeVisible] = useState(false);
  const [selectedGuarantee, setSelectedGuarantee] = useState(null);

  const TRANSLATION = t("B2C_MILTIS.PRODUCTS", { returnObjects: true });
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const adherentSocialRegime = get(contact, "socialRegime");

  const normalizeRelatedPersons = ({ spouse, children }) => {
    const normalizedChildren = (children || []).map((child) => ({
      ...child,
      identity: {
        ...get(child, "identity", {}),
        socialRegime: resolveChildSocialRegimeForPricing({
          childSocialRegime: get(child, "identity.socialRegime"),
          adherentSocialRegime,
          spouseSocialRegime: get(spouse, "identity.socialRegime"),
          hasSpouse: !!spouse,
        }),
      },
    }));

    return [spouse, ...normalizedChildren].filter(Boolean);
  };

  const onChangeRelatedPersons = async ({ spouse, children }) => {
    await refetchCosts({
      contact: {
        id: contact.id,
        relatedPersons: normalizeRelatedPersons({ spouse, children }),
      },
    });
    setAddBeneficiaries((prev) => ({ ...prev, visible: false }));
  };

  const { handleSubmit, values, ...formProps } = useFormik({
    initialValues: {
      spouse: get(contact, "relatedPersons", []).find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
      children: get(contact, "relatedPersons", []).filter(
        ({ relationship }) => relationship === RELATIONSHIP.CHILD,
      ),
    },
    onSubmit: onChangeRelatedPersons,
    validateOnMount: false,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      { adherentSocialRegime },
    ),
  });
  const [childrenNumber, setChildrenNumber] = useState(
    get(values, "children", []).length,
  );

  const [addBeneficiaries, setAddBeneficiaries] = useState({
    visible: false,
    spouse: !isEmpty(get(values, "spouse")),
    children: !isEmpty(get(values, "children")),
    status: !(
      isEmpty(get(values, "spouse")) && isEmpty(get(values, "children"))
    ),
  });
  const [disabled, setDisabled] = useState({});
  const [quoteDisabled, setQuoteDisabled] = useState(false);
  const spouseSocialRegime = get(values, "spouse.identity.socialRegime");
  const isChildrenRegimeRequired = shouldAskChildrenSocialRegime({
    adherentSocialRegime,
    spouseSocialRegime,
    hasSpouse: addBeneficiaries.spouse,
  });

  const bindSocialRegimeSelectProps = (name, { required: isRequired } = {}) => ({
    value: get(values, name),
    required: isRequired,
    errors: get(formProps.errors, name, ""),
    touched: get(formProps.touched, name, false),
    onChange: (value) => {
      formProps.setFieldValue(name, value, true);
      formProps.setFieldTouched(name, true, false);
    },
  });

  useEffect(() => {
    const children = get(values, "children", []);
    const oldChildrenLength = children.length;

    if (oldChildrenLength < childrenNumber) {
      formProps.setFieldValue("children", [
        ...children,
        {
          id: cuid(),
          relationship: RELATIONSHIP.CHILD,
          identity: {
            id: cuid(),
            user: {
              id: cuid(),
              birthDate: null,
            },
          },
        },
      ]);
    } else if (oldChildrenLength > childrenNumber) {
      formProps.setFieldValue("children", children.slice(0, childrenNumber));
    }
  }, [childrenNumber]);

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      prevRef.current.classList.add("custom-prev");
      nextRef.current.classList.add("custom-next");
    }
  }, []);

  const handleYesClick = () => {
    setAddBeneficiaries((prev) => ({
      ...prev,
      visible: true,
      status: true,
    }));
  };

  const handleNoClick = () => {
    formProps.setFieldValue("spouse", undefined);
    formProps.setFieldValue("children", []);
    setChildrenNumber(0);
    setAddBeneficiaries({
      spouse: false,
      children: false,
      visible: false,
      status: false,
    });
    if (addBeneficiaries?.status) {
      handleSubmit();
    }
  };

  return (
    <Layout>
      <Header
        title={TRANSLATION.HEADER.TITLE}
        highlight={TRANSLATION.HEADER.HIGHLIGHT}
        subtitle={TRANSLATION.HEADER.SUBTITLE}
      />

      <div className="products__page">
        <div className="relatedPersons__area">
          <div className="beneficiary__wrapper">
            <div
              className="beneficiary__wrapper--main"
              {...(isMobile && {
                onClick: () => {
                  if (isMobile) setDrawerVisible(true);
                  else
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      visible: !prev.visible,
                    }));
                },
              })}
            >
              <div className="beneficiary__wrapper--main--row">
                <div className="beneficiary__wrapper--main--left">
                  {isMobile ? (
                    <span className="question">
                      {addBeneficiaries.spouse || childrenNumber > 0
                        ? TRANSLATION.YOUR_BENEFICIARIES
                        : TRANSLATION.ADD_BENEFICIARY}
                    </span>
                  ) : (
                    <span className="question">
                      {TRANSLATION.ADD_BENEFICIARY}
                    </span>
                  )}
                </div>

                <div className="beneficiary__wrapper--main--action">
                  <Button
                    size="medium"
                    type={classNames({
                      primary: addBeneficiaries?.status,
                      transparent: !addBeneficiaries?.status,
                    })}
                    onClick={handleYesClick}
                  >
                    {TRANSLATION.YES}
                  </Button>

                  <Button
                    size="medium"
                    type={classNames({
                      primary: !addBeneficiaries?.status,
                      transparent: addBeneficiaries?.status,
                    })}
                    onClick={handleNoClick}
                  >
                    {TRANSLATION.NO}
                  </Button>
                </div>
              </div>

              {isMobile && (addBeneficiaries.spouse || childrenNumber > 0) && (
                <span className="selected--beneficiaries">
                  {addBeneficiaries.spouse && childrenNumber > 0
                    ? TRANSLATION.BENEFICIARIES.SPOUSE_AND_CHILDREN.replace(
                        "{count}",
                        childrenNumber.toString(),
                      ).replace("{plural}", childrenNumber > 1 ? "s" : "")
                    : addBeneficiaries.spouse
                      ? TRANSLATION.BENEFICIARIES.SPOUSE_ONLY
                      : childrenNumber > 0
                        ? TRANSLATION.BENEFICIARIES.CHILDREN_ONLY.replace(
                            "{count}",
                            childrenNumber.toString(),
                          ).replace("{plural}", childrenNumber > 1 ? "s" : "")
                        : ""}
                </span>
              )}
            </div>
            {!isMobile && (
              <div
                className={classNames("beneficiary__wrapper--expand", {
                  hidden: !addBeneficiaries.visible,
                })}
              >
                <div className="beneficiary__wrapper--expand--row">
                  <div className="left">
                    <Input
                      type="checkbox"
                      label={TRANSLATION.SPOUSE}
                      checked={addBeneficiaries.spouse}
                      onChange={(checked) => {
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          spouse: checked,
                        }));
                        if (checked)
                          formProps.setFieldValue("spouse", {
                            id: cuid(),
                            relationship: RELATIONSHIP.SPOUSE,
                            identity: {
                              id: cuid(),
                              user: {
                                id: cuid(),
                                birthDate: null,
                              },
                            },
                          });
                        else formProps.setFieldValue("spouse", undefined);
                      }}
                    />
                  </div>
                  <div className="miltis-form miltis-right">
                    <Input
                      type="date"
                      label={TRANSLATION.BIRTH_DATE.LABEL}
                      placeholder={TRANSLATION.BIRTH_DATE.PLACEHOLDER}
                      format={DATE_FORMAT}
                      disabled={!addBeneficiaries.spouse}
                      showToday={false}
                      {...bindDateInputProps({
                        name: "spouse.identity.user.birthDate",
                        values,
                        ...formProps,
                      })}
                      defaultPickerValue={
                        !get(values, `spouse.identity.user.birthDate`) &&
                        dayjs().subtract(24, "years")
                      }
                      disabledDate={(current) =>
                        current > isMajor || current < hundredYearsAgoStart
                      }
                    />
                    <Select
                      options={Object.values(MILTIS_SOCIAL_REGIMES)
                        .filter(
                          (option) =>
                            !territorial ||
                            option !==
                              MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                        )
                        .map((option) => {
                          const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                            (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                          );
                          return {
                            label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                            value: option,
                          };
                        })}
                      placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
                      label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
                      disabled={!addBeneficiaries.spouse}
                      {...bindSocialRegimeSelectProps(
                        "spouse.identity.socialRegime",
                        { required: true },
                      )}
                    />
                  </div>
                </div>
                <div className="divider" />
                <div className="beneficiary__wrapper--expand--row">
                  <div className="left">
                    <Input
                      type="checkbox"
                      label={TRANSLATION.CHILDREN.LABEL}
                      subLabel="Gratuité à partir du 3ème enfant."
                      checked={addBeneficiaries.children}
                      onChange={(checked) => {
                        if (!checked) {
                          setChildrenNumber(0);
                          formProps.setFieldValue("children", []);
                        } else if (childrenNumber === 0) setChildrenNumber(1);
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          children: checked,
                        }));
                      }}
                    />
                  </div>
                  <div className="miltis-form miltis-right">
                    <div className="children__count">
                      <Button
                        type={"primary"}
                        className="children__count__button"
                        onClick={() => setChildrenNumber((prev) => +prev - 1)}
                        disabled={
                          childrenNumber < 2 || !addBeneficiaries.children
                        }
                      >
                        <FaMinus size={14} />
                      </Button>
                      <div className="flex--grow-1">
                        <Input
                          type="number"
                          min={0}
                          max={12}
                          placeholder={"0"}
                          name={"gender"}
                          value={childrenNumber}
                          onChange={({ target: { value } }) => {
                            setChildrenNumber(+value);
                          }}
                          disabled={!addBeneficiaries.children}
                        />
                      </div>
                      <Button
                        type={"primary"}
                        className="children__count__button"
                        onClick={() => setChildrenNumber((prev) => +prev + 1)}
                        disabled={
                          childrenNumber > 11 || !addBeneficiaries.children
                        }
                      >
                        <FaPlus size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="children__birthday">
                  {Array(childrenNumber)
                    .fill()
                    .map((_, i) => (
                      <div
                        key={`child-${i}`}
                        className="children__birthday--row"
                      >
                        <div className="left">
                          <Label
                            label={`${
                              TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1
                            } ${TRANSLATION.CHILDREN_BIRTHDATES.CHILD}`}
                          />
                        </div>
                        <div className="miltis-form miltis-right">
                          <Input
                            label={TRANSLATION.BIRTH_DATE.LABEL}
                            placeholder={TRANSLATION.BIRTH_DATE.PLACEHOLDER}
                            type="date"
                            variant="simple"
                            format={DATE_FORMAT}
                            disabledDate={(current) => {
                              return (
                                current > dayjs() ||
                                current < twentySevenYearsAgoStart
                              );
                            }}
                            defaultPickerValue={
                              !get(
                                values,
                                `children.${i}.identity.user.birthDate`,
                              ) && dayjs().subtract(1, "years")
                            }
                            showToday={false}
                            {...bindDateInputProps({
                              name: `children.${i}.identity.user.birthDate`,
                              values,
                              ...formProps,
                            })}
                          />
                          <Select
                            options={Object.values(MILTIS_SOCIAL_REGIMES)
                              .filter(
                                (option) =>
                                  !territorial ||
                                  option !==
                                    MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                              )
                              .map((option) => {
                                const key = Object.keys(
                                  MILTIS_SOCIAL_REGIMES,
                                ).find(
                                  (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                                );
                                return {
                                  label:
                                    SOCIAL_REGIMES_TRANSLATION[key] || option,
                                  value: option,
                                };
                              })}
                            placeholder={
                              TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER
                            }
                            label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
                            {...bindSocialRegimeSelectProps(
                              `children.${i}.identity.socialRegime`,
                              { required: isChildrenRegimeRequired },
                            )}
                          />
                        </div>
                      </div>
                    ))}
                </div>
                <Button
                  block
                  type="primary"
                  onClick={() => handleSubmit()}
                  disabled={
                    !isEmpty(formProps.errors) ||
                    (childrenNumber === 0 && !addBeneficiaries.spouse)
                  }
                >
                  {TRANSLATION.SUBMIT}
                </Button>
              </div>
            )}
          </div>
          {!isMobile &&
            addBeneficiaries.status &&
            ((addBeneficiaries.spouse &&
              get(values, "spouse.identity.user.birthDate")) ||
              get(values, "children", []).some((child) =>
                get(child, "identity.user.birthDate"),
              )) && (
              <div className="miltisBeneficiary_chips">
                {addBeneficiaries.spouse &&
                  get(values, "spouse.identity.user.birthDate") && (
                    <button
                      className="miltisBeneficiary_chips--button"
                      onClick={() => {
                        formProps.setFieldValue("spouse", undefined);
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          spouse: false,
                        }));
                        handleSubmit();
                      }}
                    >
                      <span>{TRANSLATION.SPOUSE}</span>
                      <MdClose size={20} />
                    </button>
                  )}
                {get(values, "children", [])
                  .filter((child) => get(child, "identity.user.birthDate"))
                  .map((_, i) => (
                    <button
                      key={`child_${i}`}
                      className="miltisBeneficiary_chips--button"
                      onClick={() => {
                        values.children.splice(i, 1);
                        setChildrenNumber((prev) => +prev - 1);
                        if (childrenNumber === 1)
                          setAddBeneficiaries((prev) => ({
                            ...prev,
                            children: false,
                          }));
                        formProps.setFieldValue(
                          "children",
                          get(values, "children", []),
                        );
                        handleSubmit();
                      }}
                    >
                      <span>{`${TRANSLATION.CHILDREN.CHILD.TITLE} ${
                        i + 1
                      }`}</span>
                      <MdClose size={20} />
                    </button>
                  ))}
              </div>
            )}
        </div>

        <div className="products__wrapper">
          <Swiper>
            {products.map(({ id, cost, currency, insurancePlan }, i) => {
              const supportingInfo = get(
                insurancePlan,
                "supportingInformation",
                [],
              ).find(
                (item) =>
                  item.category ===
                  SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
              );

              return (
                <ProductCard
                  key={id}
                  price={{
                    cost,
                    currency,
                    period: TRANSLATION.PER_MONTH,
                  }}
                  name={get(insurancePlan, "name")}
                  description={get(insurancePlan, "description", "")}
                  icon={get(insurancePlan, "icon")}
                  showGuarantee={(guaranteeItem) => {
                    setDrawerGuaranteeVisible(true);
                    setSelectedGuarantee(guaranteeItem);
                  }}
                  action={{
                    onClick: () => {
                      setDisabled({ [id]: true });
                      onSubmit(insurancePlan.id);
                    },
                    label: TRANSLATION.SELECT_OFFER,
                    loading: disabled[id] || refetchLoading,
                    disabled: !isEmpty(disabled) || addBeneficiaries.visible,
                  }}
                  quoteAction={{
                    onClick: () => {
                      setQuoteDisabled((prev) => ({ ...prev, [id]: true }));
                      onQuoteSubmit(insurancePlan.id);
                    },
                    label: quoteDisabled[id]
                      ? "Devis envoyé"
                      : "Recevoir un devis",
                    loading: quoteLoading,
                    disabled: !!quoteDisabled[id],
                  }}
                  disabled={disabledProducts.includes(insurancePlan.id)}
                  info={{
                    title: TRANSLATION.FLAGSHIP_GUARANTEES,
                    items: Array.isArray(supportingInfo?.value)
                      ? supportingInfo.value
                      : get(insurancePlan, "fields.flagshipGuarantees", []),
                  }}
                  links={{
                    example: () => {
                      downloadSingleDocument({
                        variables: {
                          where: {
                            insurancePlans: { id: insurancePlan?.id },
                            type: ATTACHMENT_TYPES.REIMBURSEMENT_EXAMPLES,
                          },
                        },
                        onCompleted: (_) => {
                          const { document } = omitDeep(
                            _.downloadSingleDocument,
                            "__typename",
                          );
                          downloadDocument(document);
                        },
                      });
                    },
                    guarantees: () => {
                      downloadSingleDocument({
                        variables: {
                          where: {
                            insurancePlans: { id: insurancePlan?.id },
                            type: ATTACHMENT_TYPES.BROCHURE,
                          },
                        },
                        onCompleted: (_) => {
                          const { document } = omitDeep(
                            _.downloadSingleDocument,
                            "__typename",
                          );
                          downloadDocument(document);
                        },
                      });
                    },
                    ipid: () => {
                      downloadSingleDocument({
                        variables: {
                          where: {
                            insurancePlans: { id: insurancePlan?.id },
                            type: ATTACHMENT_TYPES.IPID,
                          },
                        },
                        onCompleted: (_) => {
                          const { document } = omitDeep(
                            _.downloadSingleDocument,
                            "__typename",
                          );
                          downloadDocument(document);
                        },
                      });
                    },
                  }}
                  recommendation={{
                    label: TRANSLATION.RECOMMENDATION,
                    value: i === 1,
                  }}
                />
              );
            })}
          </Swiper>
        </div>
      </div>
      <ActionButtons onBack={onBack} />
      <Drawer
        title={TRANSLATION.ADD_BENEFICIARY}
        placement="bottom"
        className="miltis__main miltis__drawer"
        closable
        onClose={async () => {
          setDrawerVisible(false);
          await refetchCosts({
            contact: {
              id: contact.id,
              relatedPersons: normalizeRelatedPersons({
                spouse: get(values, "spouse"),
                children: get(values, "children", []),
              }),
            },
          });
        }}
        open={drawerVisible}
        height="85vh"
      >
        <div className="beneficiary__wrapper">
          {(childrenNumber > 0 || addBeneficiaries.spouse) && (
            <div className="miltisBeneficiary_chips">
              {addBeneficiaries.spouse &&
                get(values, "spouse.identity.user.birthDate") && (
                  <div className="miltisBeneficiary_chips--button">
                    <span>{TRANSLATION.SPOUSE}</span>
                    <MdClose
                      size={20}
                      onClick={() => {
                        formProps.setFieldValue("spouse", undefined);
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          spouse: false,
                        }));
                        handleSubmit();
                      }}
                    />
                  </div>
                )}
              {get(values, "children", [])
                .filter((child) => get(child, "identity.user.birthDate"))
                .map((_, i) => (
                  <div
                    key={`child_${i}`}
                    className="miltisBeneficiary_chips--button"
                  >
                    <span>{`${TRANSLATION.CHILDREN.CHILD.TITLE} ${
                      i + 1
                    }`}</span>

                    <MdClose
                      onClick={() => {
                        values.children.splice(i, 1);
                        setChildrenNumber((prev) => +prev - 1);
                        if (childrenNumber === 1)
                          setAddBeneficiaries((prev) => ({
                            ...prev,
                            children: false,
                          }));
                        formProps.setFieldValue(
                          "children",
                          get(values, "children", []),
                        );
                        handleSubmit();
                      }}
                      size={20}
                    />
                  </div>
                ))}
            </div>
          )}
          <div
            className={classNames("beneficiary__wrapper--expand", {
              // hidden: !addBeneficiaries.visible,
            })}
          >
            <div className="beneficiary__wrapper--expand--row">
              <div className="left">
                <Input
                  type="checkbox"
                  label={TRANSLATION.SPOUSE}
                  checked={addBeneficiaries.spouse}
                  onChange={(checked) => {
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      spouse: checked,
                    }));
                    if (checked)
                      formProps.setFieldValue("spouse", {
                        id: cuid(),
                        relationship: RELATIONSHIP.SPOUSE,
                        identity: {
                          id: cuid(),
                          user: {
                            id: cuid(),
                            birthDate: null,
                          },
                        },
                      });
                    else formProps.setFieldValue("spouse", undefined);
                  }}
                />
              </div>
              <div className="miltis-form miltis-right">
                <Input
                  // width={261}
                  type="date"
                  label={TRANSLATION.BIRTH_DATE.LABEL}
                  placeholder={TRANSLATION.BIRTH_DATE.PLACEHOLDER}
                  format={DATE_FORMAT}
                  disabled={!addBeneficiaries.spouse}
                  showToday={false}
                  {...bindDateInputProps({
                    name: "spouse.identity.user.birthDate",
                    values,
                    ...formProps,
                  })}
                  defaultPickerValue={
                    !get(values, `spouse.identity.user.birthDate`) &&
                    dayjs().subtract(24, "years")
                  }
                  disabledDate={(current) =>
                    current > isMajor || current < hundredYearsAgoStart
                  }
                />
                <Select
                  options={Object.values(MILTIS_SOCIAL_REGIMES)
                    .filter(
                      (option) =>
                        !territorial ||
                        option !==
                          MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                    )
                    .map((option) => {
                      const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                        (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                      );
                      return {
                        label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                        value: option,
                      };
                    })}
                  placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
                  label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
                  disabled={!addBeneficiaries.spouse}
                  {...bindSocialRegimeSelectProps(
                    "spouse.identity.socialRegime",
                    { required: true },
                  )}
                />
              </div>
            </div>
            <div className="divider" />
            <div className="beneficiary__wrapper--expand--row childs">
              <div className="left">
                <Input
                  type="checkbox"
                  label={TRANSLATION.CHILDREN.LABEL}
                  checked={addBeneficiaries.children}
                  onChange={(checked) => {
                    if (!checked) {
                      setChildrenNumber(0);
                      formProps.setFieldValue("children", []);
                    } else if (childrenNumber === 0) setChildrenNumber(1);
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      children: checked,
                    }));
                  }}
                />
              </div>
              <div className="miltis-form miltis-right">
                <div className="children__count">
                  <Button
                    type={"primary"}
                    className="children__count__button"
                    onClick={() => setChildrenNumber((prev) => +prev - 1)}
                    disabled={childrenNumber < 2 || !addBeneficiaries.children}
                  >
                    <FaMinus size={14} />
                  </Button>
                  <div className="flex--grow-1">
                    <Input
                      type="number"
                      min={0}
                      max={12}
                      placeholder={"0"}
                      name={"gender"}
                      value={childrenNumber}
                      onChange={({ target: { value } }) => {
                        setChildrenNumber(+value);
                      }}
                      disabled={!addBeneficiaries.children}
                    />
                  </div>
                  <Button
                    type={"primary"}
                    className="children__count__button"
                    onClick={() => setChildrenNumber((prev) => +prev + 1)}
                    disabled={childrenNumber > 11 || !addBeneficiaries.children}
                  >
                    <FaPlus size={14} />
                  </Button>
                </div>
              </div>
            </div>
            <div className="children__birthday">
              {Array(childrenNumber)
                .fill()
                .map((_, i) => (
                  <div key={`child-${i}`} className="children__birthday--row">
                    <div className="left">
                      <Label
                        label={`${
                          TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1
                        } ${TRANSLATION.CHILDREN_BIRTHDATES.CHILD}`}
                      />
                    </div>
                    <div className="miltis-form miltis-right">
                      <Input
                        // width={"100%"}
                        label={TRANSLATION.BIRTH_DATE.LABEL}
                        placeholder={TRANSLATION.BIRTH_DATE.PLACEHOLDER}
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        disabledDate={(current) => {
                          return (
                            current > dayjs() ||
                            current < twentySevenYearsAgoStart
                          );
                        }}
                        defaultPickerValue={
                          !get(
                            values,
                            `children.${i}.identity.user.birthDate`,
                          ) && dayjs().subtract(1, "years")
                        }
                        showToday={false}
                        {...bindDateInputProps({
                          name: `children.${i}.identity.user.birthDate`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Select
                        options={Object.values(MILTIS_SOCIAL_REGIMES)
                          .filter(
                            (option) =>
                              !territorial ||
                              option !==
                                MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                          )
                          .map((option) => {
                            const key = Object.keys(
                              MILTIS_SOCIAL_REGIMES,
                            ).find(
                              (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                            );
                            return {
                              label:
                                SOCIAL_REGIMES_TRANSLATION[key] || option,
                              value: option,
                            };
                          })}
                        placeholder={
                          TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER
                        }
                        label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
                        {...bindSocialRegimeSelectProps(
                          `children.${i}.identity.socialRegime`,
                          { required: isChildrenRegimeRequired },
                        )}
                      />
                    </div>
                  </div>
                ))}
            </div>
            <Button
              block
              type="primary"
              onClick={() => {
                handleSubmit();
                setDrawerVisible(false);
              }}
              disabled={
                !isEmpty(formProps.errors) ||
                (childrenNumber === 0 && !addBeneficiaries.spouse)
              }
            >
              {TRANSLATION.SUBMIT}
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        placement="right"
        className="miltis__drawer right--drawer"
        closable
        onClose={() => setDrawerGuaranteeVisible(false)}
        open={drawerGuaranteeVisible}
        width={isMobile ? "98%" : "50%"}
      >
        {selectedGuarantee && (
          <div className="guarantees__wrapper">
            <div className="guarantees__wrapper--header">
              <div className="title">
                <span>{TRANSLATION.GUARANTEE_DRAWER.TITLE}</span>
                <h3>{selectedGuarantee.mainInfo}</h3>
              </div>
              <p>{selectedGuarantee.description}</p>
            </div>

            <div className="guarantees__wrapper--content">
              <span>{TRANSLATION.GUARANTEE_DRAWER.REIMBURSEMENT_EXAMPLE}</span>
              <h4>{selectedGuarantee.example}</h4>

              <div className="bars">
                {(() => {
                  const ss = parseFloat(
                    selectedGuarantee.details?.socialSecurity ?? 0,
                  );
                  const ins = parseFloat(
                    selectedGuarantee.details?.insurance ?? 0,
                  );
                  const rem = parseFloat(
                    selectedGuarantee.details?.remaining ?? 0,
                  );
                  const total = ss + ins + rem;

                  const getWidth = (value) => {
                    const percent = total ? (value / total) * 100 : 0;
                    return `${Math.max(percent, 10)}%`;
                  };

                  return (
                    <>
                      <div className="item social">
                        <div className="head">
                          <label>
                            {TRANSLATION.GUARANTEE_DRAWER.SOCIAL_SECURITY}
                          </label>
                          <span className="value">{ss.toFixed(2)} €</span>
                        </div>
                        <span
                          className="bar"
                          style={{ "--progress": getWidth(ss) }}
                        ></span>
                      </div>
                      <div className="item insurance">
                        <div className="head">
                          <label>{TRANSLATION.GUARANTEE_DRAWER.AMELLIS}</label>
                          <span className="value">{ins.toFixed(2)} €</span>
                        </div>
                        <span
                          className="bar"
                          style={{ "--progress": getWidth(ins) }}
                        ></span>
                      </div>
                      <div className="item remaining">
                        <div className="head">
                          <label>
                            {TRANSLATION.GUARANTEE_DRAWER.REMAINING_CHARGE}
                          </label>
                          <span className="value">{rem.toFixed(2)} €</span>
                        </div>
                        <span
                          className="bar"
                          style={{ "--progress": getWidth(rem) }}
                        ></span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </Layout>
  );
};

export default View;
